import React from 'react';

/* ─── Button ─────────────────────────────────────────────── */
export function Button({ children, variant = 'primary', size = 'md', loading, fullWidth, className = '', ...props }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: '8px', fontWeight: 600, borderRadius: '10px', transition: 'all 0.2s',
    cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
    width: fullWidth ? '100%' : undefined,
    fontFamily: 'var(--font-body)', border: 'none',
  };
  const variants = {
    primary: { background: 'var(--brand)', color: '#fff', boxShadow: '0 4px 14px rgba(255,71,87,0.35)' },
    secondary: { background: 'var(--surface-3)', color: 'var(--text-2)' },
    outline: { background: 'transparent', color: 'var(--text)', border: '1.5px solid var(--border-2)' },
    ghost: { background: 'transparent', color: 'var(--text-2)' },
    danger: { background: '#FEE2E2', color: '#EF4444' },
  };
  const sizes = {
    sm: { padding: '6px 14px', fontSize: '13px' },
    md: { padding: '10px 20px', fontSize: '14px' },
    lg: { padding: '14px 28px', fontSize: '16px' },
  };
  return (
    <button style={{ ...base, ...variants[variant], ...sizes[size] }} className={className} disabled={loading} {...props}>
      {loading && <Spinner size={14} color="currentColor" />}
      {children}
    </button>
  );
}

/* ─── Input ──────────────────────────────────────────────── */
export function Input({ label, error, icon, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {label && <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-2)' }}>{label}</label>}
      <div style={{ position: 'relative' }}>
        {icon && <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none' }}>{icon}</span>}
        <input
          style={{
            width: '100%', padding: icon ? '10px 12px 10px 38px' : '10px 14px',
            border: `1.5px solid ${error ? '#EF4444' : 'var(--border)'}`,
            borderRadius: '10px', fontSize: '14px', outline: 'none',
            background: 'var(--surface)', color: 'var(--text)',
            transition: 'border-color 0.2s',
          }}
          onFocus={e => e.target.style.borderColor = 'var(--brand)'}
          onBlur={e => e.target.style.borderColor = error ? '#EF4444' : 'var(--border)'}
          {...props}
        />
      </div>
      {error && <span style={{ fontSize: '12px', color: '#EF4444' }}>{error}</span>}
    </div>
  );
}

/* ─── Textarea ───────────────────────────────────────────── */
export function Textarea({ label, error, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {label && <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-2)' }}>{label}</label>}
      <textarea
        style={{
          width: '100%', padding: '10px 14px',
          border: `1.5px solid ${error ? '#EF4444' : 'var(--border)'}`,
          borderRadius: '10px', fontSize: '14px', outline: 'none',
          background: 'var(--surface)', color: 'var(--text)',
          resize: 'vertical', minHeight: '90px', lineHeight: 1.6,
          transition: 'border-color 0.2s',
        }}
        onFocus={e => e.target.style.borderColor = 'var(--brand)'}
        onBlur={e => e.target.style.borderColor = error ? '#EF4444' : 'var(--border)'}
        {...props}
      />
      {error && <span style={{ fontSize: '12px', color: '#EF4444' }}>{error}</span>}
    </div>
  );
}

/* ─── Select ─────────────────────────────────────────────── */
export function Select({ label, options, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {label && <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-2)' }}>{label}</label>}
      <select style={{
        width: '100%', padding: '10px 14px',
        border: '1.5px solid var(--border)', borderRadius: '10px',
        fontSize: '14px', outline: 'none', background: 'var(--surface)',
        color: 'var(--text)', cursor: 'pointer', appearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239090B0' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center',
      }} {...props}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

/* ─── Modal ──────────────────────────────────────────────── */
export function Modal({ open, onClose, title, children, width = '540px' }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(15,15,26,0.5)',
      backdropFilter: 'blur(4px)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
      animation: 'fadeIn 0.2s ease',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--surface)', borderRadius: 'var(--radius-xl)',
        width: '100%', maxWidth: width, maxHeight: '90vh',
        overflow: 'auto', boxShadow: 'var(--shadow-lg)',
        animation: 'scaleIn 0.25s cubic-bezier(0.4,0,0.2,1)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '18px', fontWeight: 700 }}>{title}</h2>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--surface-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', color: 'var(--text-2)', cursor: 'pointer', border: 'none' }}>✕</button>
        </div>
        <div style={{ padding: '24px' }}>{children}</div>
      </div>
    </div>
  );
}

/* ─── Badge ──────────────────────────────────────────────── */
export function Badge({ children, color = 'gray' }) {
  const colors = {
    gray: { bg: 'var(--surface-3)', text: 'var(--text-2)' },
    red: { bg: '#FEE2E2', text: '#DC2626' },
    green: { bg: '#D1FAE5', text: '#059669' },
    blue: { bg: '#DBEAFE', text: '#2563EB' },
    orange: { bg: '#FEF3C7', text: '#D97706' },
    purple: { bg: '#EDE9FE', text: '#7C3AED' },
  };
  const c = colors[color] || colors.gray;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '50px', fontSize: '12px', fontWeight: 600, background: c.bg, color: c.text }}>
      {children}
    </span>
  );
}

/* ─── Stars ──────────────────────────────────────────────── */
export function Stars({ note, max = 5, size = 16, interactive, onChange }) {
  const [hover, setHover] = React.useState(0);
  const display = hover || note || 0;
  return (
    <span style={{ display: 'inline-flex', gap: '2px' }}>
      {Array.from({ length: max }, (_, i) => {
        const val = i + 1;
        return (
          <span key={i}
            onMouseEnter={interactive ? () => setHover(val) : undefined}
            onMouseLeave={interactive ? () => setHover(0) : undefined}
            onClick={interactive ? () => onChange?.(val) : undefined}
            style={{ fontSize: size, color: val <= display ? '#F59E0B' : '#E0E0F0', cursor: interactive ? 'pointer' : 'default', transition: 'color 0.1s', lineHeight: 1 }}
          >★</span>
        );
      })}
    </span>
  );
}

/* ─── Spinner ────────────────────────────────────────────── */
export function Spinner({ size = 20, color = 'var(--brand)' }) {
  return (
    <span style={{
      display: 'inline-block', width: size, height: size,
      border: `2px solid ${color}30`, borderTopColor: color,
      borderRadius: '50%', animation: 'spin 0.7s linear infinite', flexShrink: 0,
    }} />
  );
}

/* ─── Prix ───────────────────────────────────────────────── */
export function Prix({ niveau }) {
  const map = { 1: '€', 2: '€€', 3: '€€€', 4: '€€€€' };
  const label = map[niveau] || '€';
  return (
    <span style={{ fontWeight: 700, color: '#059669', fontSize: '13px' }}>{label}</span>
  );
}

/* ─── Statut Badge ───────────────────────────────────────── */
export function StatutBadge({ statut }) {
  if (statut === 'tester') return <Badge color="orange">📌 À tester</Badge>;
  return <Badge color="green">✅ Testé</Badge>;
}

/* ─── Empty State ────────────────────────────────────────── */
export function EmptyState({ icon, title, subtitle, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px', animation: 'fadeIn 0.4s ease' }}>
      <div style={{ fontSize: '52px', marginBottom: '16px' }}>{icon}</div>
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 700, marginBottom: '8px' }}>{title}</h3>
      <p style={{ color: 'var(--text-3)', marginBottom: '24px', fontSize: '15px' }}>{subtitle}</p>
      {action}
    </div>
  );
}

/* ─── Toast ──────────────────────────────────────────────── */
export function Toast({ message, type = 'success', onClose }) {
  React.useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  const colors = { success: { bg: '#ECFDF5', border: '#10B981', text: '#065F46' }, error: { bg: '#FEF2F2', border: '#EF4444', text: '#991B1B' } };
  const c = colors[type];
  return (
    <div style={{
      position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
      background: c.bg, border: `1px solid ${c.border}`, color: c.text,
      padding: '12px 20px', borderRadius: '12px', fontSize: '14px', fontWeight: 500,
      boxShadow: 'var(--shadow-lg)', maxWidth: '360px',
      animation: 'slideUp 0.3s cubic-bezier(0.4,0,0.2,1)',
      display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span>{type === 'success' ? '✅' : '❌'}</span>
      {message}
    </div>
  );
}
